import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Key, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function PasswordResetConfirmPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
    if (password.length < 6) { toast.error('Mindestens 6 Zeichen'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      setDone(true);
    } catch (err) {
      toast.error(err.message || 'Fehler beim Zurücksetzen');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-sm w-full">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Ungültiger Link. Bitte fordern Sie einen neuen Passwort-Reset an.</p>
            <Link to="/passwort-vergessen"><Button variant="outline" className="mt-4">Neuen Link anfordern</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-6">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg tracking-tight">VerwalterHonorar.de</span>
        </div>
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto h-10 w-10 rounded-full bg-secondary flex items-center justify-center mb-2">
              <Key className="h-4 w-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg">Neues Passwort vergeben</CardTitle>
            <CardDescription>Legen Sie ein neues Passwort fest.</CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="text-center space-y-4" data-testid="reset-done">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
                <p className="text-sm text-muted-foreground">Passwort erfolgreich geändert!</p>
                <Link to="/login">
                  <Button className="mt-2" data-testid="goto-login-after-reset">Jetzt anmelden</Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-pw">Neues Passwort</Label>
                  <Input id="new-pw" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 Zeichen" required minLength={6} data-testid="new-pw-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-pw">Passwort bestätigen</Label>
                  <Input id="confirm-pw" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Passwort wiederholen" required data-testid="confirm-pw-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading || !password} data-testid="set-new-pw-btn">
                  {loading ? 'Speichern...' : 'Neues Passwort speichern'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
